import { inject, Injectable } from '@angular/core';
import { map, type Observable, take } from 'rxjs';

import {
  type LogicalQueryNode,
  type LogicalType,
  type SortRequest,
  type WorkflowPredicate,
} from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';

export interface DatasetRowsRequest {
  condition: string;
  context: RuntimeContext;
  datasetId: string;
  maxResults?: number;
  queryConditions: WorkflowPredicate[];
  queryLogicalOp?: LogicalType;
  queryNode: LogicalQueryNode | undefined;
  sort?: SortRequest;
  /** Reads the named list off each matched row instead of the rows themselves. */
  subDataset: string;
  workflowExecutionId?: string;
  workflowId?: string;
}

/**
 * Fetches dataset rows for the nodes that read them — `Get` directly, `Lookup` per batch of keys.
 *
 * The node's query is evaluated against the runtime context here, so callers pass the context
 * rather than an already-built filter.
 */
@Injectable({ providedIn: 'root' })
export class DatasetRowsQueryService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly iss = inject(IntelliSenseService);

  getDatasetRows({
    condition,
    context,
    datasetId,
    maxResults,
    queryConditions,
    queryLogicalOp,
    queryNode,
    sort,
    subDataset,
    workflowExecutionId,
    workflowId,
  }: DatasetRowsRequest): Observable<{ data: any }> {
    const filterQuery = this.iss.createDatasetFilter(
      queryNode,
      queryConditions,
      condition,
      queryLogicalOp,
      context,
    );

    return this.datasetRowsService
      .get({
        datasetId,
        filterQuery,
        includeRowId: true,
        maxResults,
        showThemedNotification: true,
        sort,
        workflowExecutionId,
        workflowId,
      })
      .pipe(
        take(1),
        map((data) =>
          Array.isArray(data) && subDataset
            ? { data: collectSubDataset(data, subDataset) }
            : { data },
        ),
      );
  }
}

/** Flattens the named list off every row; a row carrying a single value contributes just that. */
function collectSubDataset(rows: DatasetRow[], subDataset: string): DatasetRow[] {
  return rows.flatMap((row) => {
    const subRow = row[subDataset];
    if (Array.isArray(subRow)) {
      return subRow;
    }
    return subRow ? [subRow] : [];
  });
}
